import { useEffect, useState } from 'react'
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { fetchAuthSession } from '@aws-amplify/auth'

import Breadcrumbs from '../../components/breadcrumbs/Breadcrumbs'
import GridTable from '../../components/GridTable/GridTable'
import { TableDetailsItem } from '../../components/GridTable/GridTableFunctions'
import CustomModal from '../../components/CustomModal'
import PinVerificationModel from '../../components/Models/PinVerificationModel'
import TransactionReceipt from '../RaiseETopUpRequest/TransactionReceipt'

import { RootState } from '../../store'
import { setStaticData, clearStaticData } from '../../store/slices/bulkTransferSlice'
import { showToast } from '../../components/toastMessage/toastSlice'
import useApi from '../../services/useApi'
import { v4 as uuidv4 } from 'uuid'

/* ───────────────────────── End-points ───────────────────────── */
const WALLET_TRANSFER_ENDPOINT = 'merchant-recharge/wallet/transfer'
const RECHARGE_BULK_ENDPOINT = 'merchant-recharge/bulk-mno'
const BATCH_RECHARGE_ENDPOINT = 'merchant-recharge/batch-recharge'

/* ================================================================= */
function ListWallet() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const dispatch = useDispatch()
  const { PostAPI } = useApi()

  /* ───────────────────── Table type detection ──────────────────── */
  const urlType = searchParams.get('type') as 'wallet-transfer' | 'recharge' | null
  const [tableType, setTableType] = useState<'wallet-transfer' | 'recharge'>(
    urlType || 'wallet-transfer'
  )

  /* ───────────────────── Redux data ────────────────────────────── */
  const staticData = useSelector((s: RootState) => s.bulkTransfer.staticData)
  /* ───────────────────── UI state / modals ─────────────────────── */
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [isVerificationModelOpen, setIsVerificationModelOpen] = useState(false)
  const [isTransactionReceipt, setIsTransactionReceipt] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteData, setDeleteData] = useState<any>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [pin, setPin] = useState<string[]>(['', '', '', ''])
  const [verifyLoading, setVerifyLoading] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [responseData, setResponseData] = useState<any>({})
  const [rowToDelete, setRowToDelete] = useState<{
    id: string
    user_code?: string
    wallet_type?: string
    amount: number
    name?: string
    phone_number?: string
  } | null>(null)

  const [formData, setFormData] = useState({
    pin_interval: '',
    isDontAskAgainChecked: false,
  })
  const [pinErrors, setPinErrors] = useState<{pin_interval?: string}>({})

  /* Totals */
  const totalAmount = staticData.reduce((s, r: any) => s + (+r.amount || 0), 0)
  const userCount = staticData.length

  /* ───────────────────── Load initial data (from nav) ──────────── */
  useEffect(() => {
    const navData = location.state?.responseData
    if (urlType) {
      setTableType(urlType)
    } else if (tableType && !searchParams.get('type')) {
      setSearchParams(prev => ({ ...Object.fromEntries(prev), type: tableType }))
    }
    if (navData) {
      let data = navData.data || navData

      /* auto detect table type */
      if (!urlType && data?.length) {
        const f = data[0]
        if (Object.prototype.hasOwnProperty.call(f, 'name') && Object.prototype.hasOwnProperty.call(f, 'phone_number')) {
          setTableType('recharge')
        } else if (Object.prototype.hasOwnProperty.call(f, 'user_code') && Object.prototype.hasOwnProperty.call(f, 'wallet_type')) {
          setTableType('wallet-transfer')
        }
      }

      /* recharge prettifying */
      if ((urlType === 'recharge' || tableType === 'recharge') && data?.length) {
        data = data.map((item: any, index: number) => {
          const processedItem = { ...item, id: uuidv4(), _clientId: `recharge_${Date.now()}_${index}` }
          
          // Combine country_code with phone_number
          if (item.country_code && item.phone_number) {
            processedItem.phone_number_with_country_code = `${item.country_code} ${item.phone_number}`
          }
          
          // Extract telecom name
          if (item.data?.name) {
            processedItem.telecom_name = item.data.name
          }
          
          // Update wallet type display
          processedItem.wallet_type = item.wallet_type === 'etopup' ? 'eTop Up' : 'Digital Wallet'
          
          // Validate amount against MNO rules
          if (item.data?.min_recharge_amount && item.data?.max_recharge_amount) {
            const amount = Number(item.amount)
            const minAmount = item.data.min_recharge_amount
            const maxAmount = item.data.max_recharge_amount
            
            if (amount < minAmount || amount > maxAmount) {
              processedItem.message = 'Min/max recharge amount criteria not met (MNO rule)'
              processedItem.status = 'invalid' // Override API status for amount validation failure
            } else {
              processedItem.message = '-' // Clear error message if validation passes
              processedItem.status = 'valid'
            }
          } else {
            // Set default status if no MNO rules
            processedItem.message = processedItem.message || '-'
            processedItem.status = processedItem.status || 'valid'
          }
          
          return processedItem
        })
      } else if (data?.length) {
        // Add id to wallet transfer data and transform wallet_type
        data = data.map((item: any, index: number) => {
          const processedItem = { 
            ...item, 
            id: uuidv4(), 
            _clientId: `wallet_${Date.now()}_${index}`,
            status: item.status || 'valid'
          }
          // Update wallet type display format for wallet transfer
          if (item.wallet_type) {
            processedItem.wallet_type = item.wallet_type === 'etopup' ? 'eTop Up' : 'Digital Wallet'
          }
          return processedItem
        })
      }
      
      dispatch(setStaticData(data))
    }
  }, [location.state, urlType, tableType, dispatch])

  /* ───────────────────── Check PIN validity ────────────────────── */
  const checkPinValidity = async () => {
    try {
      const user = await fetchAuthSession({ forceRefresh: true })
      const timestamp = user?.tokens?.idToken?.payload["custom:pin_expiry_timestamp"]
      
      if (timestamp) {
        const inputDate = new Date(timestamp)
        const currentDate = new Date()
        return inputDate >= currentDate
      }
      return false
    } catch (error) {
      console.error('PIN check failed:', error)
      return false
    }
  }

  /* ───────────────────── Payload builder ───────────────────────── */
  const makePayload = () => {
    if (!staticData.length) return null
    
    if (tableType === 'wallet-transfer') {
      return {
        pin: pin.join(''),
        pin_interval: formData.pin_interval || undefined,
        records: staticData.map((r: any) => ({
          user_code: r.user_code.trim(), // Already clean user code from edit-wallet
          wallet_type: r.wallet_type === 'eTop Up' ? 'etopup' : 'digital', // Map display to API values
          amount: +r.amount,
        })),
      }
    }
    
    return {
      pin: pin.join(''),
      pin_interval: formData.pin_interval || undefined,
      records: staticData.map((r: any) => ({
        country_code: r.country_code || '+93',
        phone_number: r.phone_number,
        amount: +r.amount,
        wallet_type: r.wallet_type === 'eTop Up' ? 'etopup' : 'digital',
      })),
    }
  }

  /* ───────────────────── Make batch recharge payload ───────────── */
  const makeBatchRechargePayload = () => {
    return {
      recharges: staticData.map((record: any) => ({
        name: record.name || '',
        country_code: record.country_code || '',
        phone_number: record.phone_number || '',
        amount: Number(record.amount) || 1,
        wallet_type: record.wallet_type === 'eTop Up' ? 'etopup' : 'digital',
        location: record.location || '',
        company_name: record.company_name || '',
        father_name: record.father_name || ''
      })),
      pin: pin.join(''),
      pin_interval: formData.pin_interval || ''
    }
  }

  /* ───────────────────── Handle Table Actions ──────────────────── */
  const handleActions = (id: string, action: string, row: any) => {
    if (action === 'edit') {
      navigate(`/bulk-transfer/edit-record?type=${tableType}`, {
        state: { editData: row },
      })
    } else if (action === 'delete') {
      // Prevent deletion if it's the last record
      if (staticData.length <= 1) {
        return // Do nothing if it's the last record
      }
      // Use UUID-based deletion for both recharge and wallet transfer
      setDeleteData(row.id)
      setRowToDelete(row)
      setShowDeleteModal(true)
    }
  }

  /* ───────────────────── Handle Delete ─────────────────────────── */
  const handleConfirmDelete = async () => {
    if (deleteData !== null) {
      setDeleteLoading(true)
      try {
        // Remove item by UUID for both recharge and wallet transfer
        const updatedData = staticData.filter((item: any) => item.id !== deleteData)
        dispatch(setStaticData(updatedData))
        dispatch(showToast({ type: 'success', message: 'Record deleted successfully!' }))
        
        // Check if total records is at pagination boundary (11, 21, etc)
        const currentPage = parseInt(searchParams.get('page_number') || '1');
        if (staticData.length % 10 === 1) {
          // Set page to previous page if we're deleting last item of current page
          setSearchParams({
            ...Object.fromEntries(searchParams),
            page_number: (currentPage - 1).toString()
          });
        }

        setShowDeleteModal(false)
        setDeleteData(null)
        setRowToDelete(null)
      } catch (error) {
        console.error('Failed to delete record:', error)
        dispatch(showToast({ type: 'error', message: 'Failed to delete record. Please try again.' }))
      } finally {
        setDeleteLoading(false)
      }
    }
  }

  /* ───────────────────── Confirm (modal #1) ────────────────────── */
  const handleConfirmTransfer = async () => {
    setConfirmLoading(true)
    
    try {
      // Check if there's data to process
      if (!staticData || staticData.length === 0) {
        dispatch(showToast({ 
          type: 'error', 
          message: `No data available for ${tableType === 'recharge' ? 'recharge' : 'transfer'}.` 
        }))
        return
      }

      // For recharge type, check if PIN is needed
      if (tableType === 'recharge') {
        const isPinValid = await checkPinValidity()
        if (!isPinValid) {
          setIsPopupOpen(false)
          resetPinState()
          setIsVerificationModelOpen(true)
          return
        }
        // PIN is valid, proceed without verification
        await processTransfer(false)
      } else {
        // For wallet transfer, always show PIN verification
        setIsPopupOpen(false)
        resetPinState()
        setIsVerificationModelOpen(true)
      }
    } finally {
      setConfirmLoading(false)
    }
  }

  /* ───────────────────── Reset PIN state ───────────────────────── */
  const resetPinState = () => {
    setPin(['', '', '', ''])
    setErrorMessage('')
    setFormData({ pin_interval: '', isDontAskAgainChecked: false })
    setPinErrors({})
  }

  /* ───────────────────── Process Transfer ──────────────────────── */
  const processTransfer = async (requirePin: boolean) => {
    setVerifyLoading(true)
    setErrorMessage('')

    try {
      // Validate PIN if required
      if (requirePin) {
        if (pin.includes('')) {
          setErrorMessage('This field is required')
          return
        }
        if (formData.isDontAskAgainChecked && !formData.pin_interval) {
          setPinErrors({ pin_interval: 'This field is required' })
          return
        }
      }

      let payload: any
      let endpoint: string

      if (tableType === 'recharge') {
        // Use batch-recharge endpoint for recharge
        payload = requirePin ? makeBatchRechargePayload() : {
          ...makeBatchRechargePayload(),
          pin: '',
          pin_interval: ''
        }
        endpoint = BATCH_RECHARGE_ENDPOINT
      } else {
        // Use wallet transfer endpoint
        payload = makePayload()
        if (!payload) {
          setErrorMessage('No records to transfer.')
          return
        }
        endpoint = WALLET_TRANSFER_ENDPOINT
      }

      const res = await PostAPI(endpoint, payload)

      if (res.error) {
        const statusCode = res.data?.response?.status
        const errorMsg = res.data?.response?.data?.message || 'Transfer failed. Try again.'
        
        // Only keep PIN modal open for 403 with PIN-related error messages
        if (statusCode === 403 && errorMsg.toLowerCase().includes('invalid pin')) {
          setErrorMessage(errorMsg)
          return
        }
        
        // For all other errors, close modal and show toast
        setIsVerificationModelOpen(false)
        resetPinState()
        dispatch(showToast({
          type: 'error',
          message: errorMsg
        }))
        return
      }

      /* success */
      setResponseData(res.data)
      setIsVerificationModelOpen(false)
      setIsPopupOpen(false)
      setIsTransactionReceipt(true)
    } catch (err: any) {
      const errorMsg = err?.message || 'Unexpected error'
      // Close modal and show toast for unexpected errors too
      setIsVerificationModelOpen(false)
      resetPinState()
      dispatch(showToast({
        type: 'error',
        message: errorMsg
      }))
    } finally {
      setVerifyLoading(false)
      if (!requirePin) resetPinState()
    }
  }

  /* ───────────────────── Verify PIN & call API ─────────────────── */
  const handleClickVerify = () => processTransfer(true)

  /* ───────────────────── Handle PIN input change ───────────────── */
  const handlePinInputChange = (key: string, value: string | boolean) => {
    if (key === 'isDontAskAgainChecked') {
      setFormData(prev => ({ ...prev, isDontAskAgainChecked: value as boolean }))
      if (!value) {
        setFormData(prev => ({ ...prev, pin_interval: '' }))
        setPinErrors(prev => ({ ...prev, pin_interval: '' }))
      }
    } else if (key === 'pin_interval') {
      setFormData(prev => ({ ...prev, pin_interval: value as string }))
      setPinErrors(prev => ({ ...prev, pin_interval: '' }))
    }
  }

  /* ───────────────────── Column definitions ────────────────────── */
  const WalletCols: Record<string, TableDetailsItem> = {
    'User Code': { id: 'user_code', className: 'min-w-[140px]' },
    'Wallet Type': { id: 'wallet_type', className: 'min-w-[140px]' },
    Amount: { id: 'amount', custom: ['amount'], className: 'min-w-[100px]' },
    'Error Message': {
      id: 'error_message',
      className: 'min-w-[300px]',
      render: (v: string, r: any) => (r.status === 'valid' ? '_' : v || ''),
    },
    Actions: {
      id: 'actions',
      className: 'min-w-[200px]',
      actionTypes: ['edit', 'delete'],
    },
  }

  const RechargeCols: Record<string, TableDetailsItem> = {
    'Customer Phone Number': { 
      id: 'phone_number_with_country_code', 
      className: 'min-w-[140px]' 
    },
    'Customer Name': { id: 'name', className: 'min-w-[140px]' },
    'Father Name': { id: 'father_name', className: 'min-w-[140px]' },
    'Recharge Amount': { id: 'amount', custom: ['amount'], className: 'min-w-[100px]' },
    Location: { id: 'location', className: 'min-w-[140px]' },
    'Company Name': { id: 'company_name', className: 'min-w-[140px]' },
    'Wallet Type': { id: 'wallet_type', className: 'min-w-[140px]' },
    'Error Description': { id: 'message', className: 'min-w-[300px]' },
    Telecom: { id: 'telecom_name', className: 'min-w-[140px]' },
    Actions: {
      id: 'actions',
      className: 'min-w-[200px]',
      actionTypes: ['edit', 'delete'],
    },
  }
  /* ───────────────────── Render ────────────────────────────────── */
  return (
    <div>
      {/* Breadcrumbs */}
      <Breadcrumbs
        navPath={
          tableType === 'recharge'
            ? ['Bulk Transfer', 'Recharge', 'List of Recharge Transfer']
            : ['Bulk Transfer', 'Wallet Transfer', 'List of Wallet Transfer']
        }
        heading={
          tableType === 'recharge'
            ? 'List of Recharge Bulk Transfer'
            : 'List of Wallet Bulk Transfers'
        }
        noNavigation
        links={
          tableType === 'recharge'
            ? ['/bulk-transfer?type=recharge', '/bulk-transfer?type=recharge']
            : [
                '/bulk-transfer?type=wallet-transfer',
                '/bulk-transfer?type=wallet-transfer',
              ]
        }
        hasAddPrivilege
        button={{
          buttonName: tableType === 'recharge' ? 'Recharge' : 'Transfer',
          buttonHeight: 'h-[45px]',
          buttonWidth: 'w-[175px]',
          className:'px-4 py-2',
          buttonOnclick: () => {
            // Validation for invalid records
            const hasErrors = staticData.some((r: any) => 
              r.status === 'invalid' || (r.status !== 'valid' && r.status !== 'success')
            )
            
            if (hasErrors) {
              dispatch(
                showToast({
                  type: 'error',
                  message: 'Please resolve all the errors before proceeding with the operation.',
                })
              )
              return
            }
            
            setIsPopupOpen(true)
          },
          loading: verifyLoading,
        }}
      />

      {/* Table */}
      <div className="bg-white h-[calc(100vh-225px)] rounded-lg">
        <GridTable
          staticText={{
            emptyScreenText: {
              heading1:
                tableType === 'recharge'
                  ? 'No Recharge Data'
                  : 'No Transfer Data',
              heading2:
                tableType === 'recharge'
                  ? 'No recharge data available'
                  : 'No wallet transfer data available',
            },
          }}
          mainEndPoint=""
          TableDetails={tableType === 'recharge' ? RechargeCols : WalletCols}
          handleActions={handleActions}
          filters={false}
          exporting={false}
          staticData={staticData}
          data={staticData}
          hideTopBar
          hasEditMDPrivilege
          hasViewMDPrivilege
          paginateMarginTop={8}
          forceFullWidth={true}
        />
      </div>

      {/* Confirm modal */}
      {isPopupOpen && (
        <CustomModal
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          title={tableType === 'recharge' ? 'Confirm Recharge' : 'Confirm Bulk Transfer'}
          subText={
            tableType === 'recharge'
              ? `You're recharging ${userCount} phone numbers for a total of AFA ${totalAmount.toFixed(2)}.`
              : `You're about to transfer a total of AFA ${totalAmount.toFixed(2)} to ${userCount} record(s).`
          }
          onHandleClose={() => setIsPopupOpen(false)}
          primaryButtonText={tableType === 'recharge' ? 'Recharge' : 'Proceed'}
          secondaryButtonText="Cancel"
          handelSubmit={handleConfirmTransfer}
          buttonColor="bg-p_primary"
          buttonLoading={confirmLoading}
          buttonWidth="w-[108px]"
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteData !== null && (
        <CustomModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false)
            setDeleteData(null)
            setRowToDelete(null)
          }}
          title="Confirm Deletion"
          subText={
            tableType === 'recharge'
              ? `You're about to delete the details for ${rowToDelete?.name || 'N/A'} (Phone: ${rowToDelete?.phone_number_with_country_code || 'N/A'}) with a recharge amount of AFA ${(rowToDelete?.amount || 0).toFixed(2)}. Are you sure you want to proceed?`
              : `You're about to delete the wallet transfer for ${rowToDelete?.user_code || 'N/A'} with an amount of AFA ${(rowToDelete?.amount || 0).toFixed(2)}. Are you sure you want to proceed?`
          }
          onHandleClose={() => {
            setShowDeleteModal(false)
            setDeleteData(null)
            setRowToDelete(null)
          }}
          primaryButtonText="Delete"
          secondaryButtonText="Cancel"
          handelSubmit={handleConfirmDelete}
          buttonLoading={deleteLoading}
          buttonWidth="w-24"
        />
      )}

      {/* PIN modal */}
      {isVerificationModelOpen && (
        <PinVerificationModel
          handleClose={() => {
            setIsVerificationModelOpen(false)
            resetPinState()
          }}
          pin={pin}
          setPin={setPin}
          handleDropdownChange={handlePinInputChange}
          selectedDropDownValue={formData.pin_interval}
          isDontAskAgainChecked={formData.isDontAskAgainChecked}
          handleClickVerify={handleClickVerify}
          verifyLoading={verifyLoading}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          dropdownError={pinErrors.pin_interval}
        />
      )}

      {/* Receipt */}
      {isTransactionReceipt && (
        <TransactionReceipt
          details={{
            ...responseData,
            description:
              tableType === 'recharge'
                ? 'Recharges are being processed and may take some time.'
                : 'Wallet transfer are being processed and may take some time.',
          }}
          handleClickDone={() => {
            navigate('/bulk-transfer')
            setIsTransactionReceipt(false)
            dispatch(clearStaticData())
          }}
          defaultPaymentType="paid"
          headerMessage={
            tableType === 'recharge'
              ? 'Bulk Recharge Completed'
              : 'Bulk Transfer Completed'
          }
          bulkTransfer
          handleCancel={() => {
            navigate('/bulk-transfer')
            setIsTransactionReceipt(false)
            dispatch(clearStaticData())
          }}
          handleGoToDashboard={() => {
            navigate('/dashboard')
            setIsTransactionReceipt(false)
            dispatch(clearStaticData())
          }}
          customWidth="w-[28.81rem]" // 461px converted to rem (461/16 = 28.8125)
          customHeight="h-[20.70rem]" // 324px converted to rem (324/16 = 20.25)
        />
      )}
    </div>
  )
}

export default ListWallet